import { ApiError } from '@/api/client';
import { isDecisionsModel } from '@/components/DecisionsBadge';
import { fmtUsd } from '@/lib/format';
import type { AdminModel, ModelKind } from '@/types';

/** What a discover result or OpenRouter lookup hands to the add form. */
export interface ModelPrefill {
  supplier: string;
  modelId: string;
  displayName: string;
  kind?: ModelKind;
  maxContextTokens?: number | null;
  inputCostPerMillionUsd?: number | null;
  outputCostPerMillionUsd?: number | null;
  cacheReadCostPerMillionUsd?: number | null;
  cacheWriteCostPerMillionUsd?: number | null;
  priceSource?: string | null;
  supportsImageInput?: boolean | null;
  supportsDocumentInput?: boolean | null;
  supportsFileTasks?: boolean;
  supportsEffort?: boolean | null;
}

/** Suppliers whose upstream list-models endpoint the gateway can query (OpenRouter is looked up by slug). */
export const discoverSuppliers = ['Anthropic', 'OpenAi', 'AzureOpenAi', 'DeepSeek', 'MiniMax', 'Kimi', 'Grok'];

/** The API's own explanation where there is one (problem-details detail), else the message. */
export function apiErrorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.detail ?? e.message;
  return e instanceof Error ? e.message : 'Something went wrong.';
}

export function parsePrice(s: string): number {
  return s.trim() === '' ? 0 : Number(s);
}

export function parseOptional(s: string): number | null {
  return s.trim() === '' ? null : Number(s);
}

/** True when a typed number is present but not a valid non-negative number. */
export function badNumber(s: string): boolean {
  return s.trim() !== '' && (Number.isNaN(Number(s)) || Number(s) < 0);
}

export interface Capabilities {
  images: boolean;
  documents: boolean;
  fileTasks: boolean;
  effort: boolean;
  effortWithTools: boolean;
}

/** Plain-language descriptions, shared by the model page and the add dialog. */
export const capabilityText: Record<keyof Capabilities, { title: string; desc: string }> = {
  images: { title: 'Images', desc: 'Callers can attach JPEG, PNG, GIF or WebP images to a message.' },
  documents: { title: 'PDFs', desc: 'Callers can attach PDF documents. Only Anthropic, OpenAI and Azure OpenAI wire formats carry them.' },
  fileTasks: { title: 'File tasks', desc: 'Can run on the File tasks page, which works on files in a code sandbox (Anthropic code execution only).' },
  effort: { title: 'Reasoning effort', desc: 'Honours the effort setting (low to max). Ignored silently by models without it.' },
  effortWithTools: {
    title: 'Effort while using tools',
    desc: 'Accepts effort and tools on the same call. Switch off for models that refuse the combination (OpenAI gpt-5.6): effort is then dropped on tool-calling turns so the call still succeeds.'
  }
};

/**
 * The capability switches with a sentence each. "Effort while using tools" sits under effort and
 * only means something while effort is on.
 */
export function CapabilitySwitches({ value, onChange, idPrefix }: {
  value: Capabilities;
  onChange: (v: Capabilities) => void;
  idPrefix: string;
}) {
  const row = (key: keyof Capabilities, nested = false, disabled = false) => {
    const id = `${idPrefix}-${key}`;
    return (
      <div className={`cap-row${nested ? ' nested' : ''}${disabled ? ' disabled' : ''}`} key={key}>
        <label className="switch" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={value[key]}
            disabled={disabled}
            onChange={e => onChange({ ...value, [key]: e.target.checked })}
            aria-describedby={`${id}-desc`}
          />
          <span className="track"></span>
        </label>
        <div className="cap-text">
          <label htmlFor={id} className="cap-title">{capabilityText[key].title}</label>
          <div className="cap-desc" id={`${id}-desc`}>
            {capabilityText[key].desc}
            {disabled && ' Turn on reasoning effort first.'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {row('images')}
      {row('documents')}
      {row('fileTasks')}
      {row('effort')}
      {row('effortWithTools', true, !value.effort)}
    </div>
  );
}

/** Capabilities as text chips (never emoji): what a model accepts beyond plain text. */
export function AcceptsChips({ images, documents, fileTasks, effort }: {
  images?: boolean | null; documents?: boolean | null; fileTasks?: boolean | null; effort?: boolean | null;
}) {
  const chips = [
    images ? 'Images' : null,
    documents ? 'PDFs' : null,
    fileTasks ? 'File tasks' : null,
    effort ? 'Effort' : null
  ].filter((c): c is string => !!c);
  if (chips.length === 0) return <span className="text-muted">Text only</span>;
  return <>{chips.map(c => <span key={c} className="chip">{c}</span>)}</>;
}

/** "In / out per 1M" — Decisions models bill input only, so their output reads "free". */
export function PriceInOut({ model }: { model: Pick<AdminModel, 'kind' | 'inputCostPerMillionUsd' | 'outputCostPerMillionUsd'> }) {
  return (
    <>
      {fmtUsd(model.inputCostPerMillionUsd)} /{' '}
      {isDecisionsModel(model) ? <span className="free">free</span> : fmtUsd(model.outputCostPerMillionUsd)}
    </>
  );
}

/** Deactivated with nothing to serve in its place: callers asking for it get an error. */
export function needsAttention(m: AdminModel): boolean {
  return !!m.deactivatedAtUtc && m.alternativeModelId == null;
}
